<template>
  <div class="step-container">
    <span class="step-title">Set post-release destinations</span>
    <span class="step-subtitle"
      >Make a stunning landing page and engage with your fans.</span
    >
    <div class="step-content">
      <span class="description">
        Any destinations with links will show on your landing page.
      </span>
      <div class="step-layout">
        <div class="destinations-container">
          <div class="destinations">
            <draggable v-model="edited_destinations" handle=".handle-icon">
              <div
                v-for="(destination, index) in edited_destinations"
                :key="destination.id"
              >
                <div class="destination-container" v-if="destination.enabled">
                  <div class="handle-icon">
                    <DraghandleIcon />
                  </div>
                  <div class="destination">
                    <div class="top">
                      <template v-if="destination.type === 'message_optin'">
                        <div class="destination-logo">
                          <img
                            :src="getDestinationImage(destination)"
                            :alt="destination.name"
                          />
                        </div>
                      </template>
                      <template v-else-if="destination.type === 'header'">
                        <div class="destination-logo">
                          <div class="h">
                            H
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="destination-logo">
                          <img
                            :src="getDestinationImage(destination)"
                            :alt="destination.name"
                          />
                        </div>
                      </template>
                      <div
                        class="destination-title"
                        v-if="destination.type === 'message_optin'"
                      >
                        Collect Subscribers
                      </div>
                      <div
                        class="destination-title"
                        v-if="destination.type === 'header'"
                      >
                        Header
                      </div>
                      <div
                        class="url-field url-field_full"
                        v-if="destination.type !== 'message_optin'"
                      >
                        <label
                          class="default-input"
                          v-if="destination.type === 'header'"
                        >
                          <input
                            v-model="destination.text"
                            type="text"
                            placeholder="Header"
                          />
                        </label>
                        <label class="default-input" v-else>
                          <input
                            v-model="destination.url"
                            type="text"
                            placeholder="URL"
                          />
                        </label>
                      </div>
                      <div
                        class="destination-action"
                        @click="resetDestination(index)"
                        v-if="
                          !['message_optin', 'header'].includes(
                            destination.type
                          )
                        "
                      >
                        <font-awesome-icon
                          :icon="['fas', 'rotate']"
                          :spin="destination.resetting"
                        />
                      </div>
                      <div
                        class="destination-action"
                        :class="{ active: destination.showDeeplinks }"
                        @click="toggleShowDeeplinks(index)"
                        v-if="
                          !['message_optin', 'header'].includes(
                            destination.type
                          )
                        "
                      >
                        <font-awesome-icon
                          :icon="['fas', 'mobile-screen-button']"
                        />
                      </div>
                      <div
                        class="destination-action"
                        @click="removeDestination(index)"
                      >
                        <RemoveIcon />
                      </div>
                    </div>
                    <div
                      class="destination-expanded"
                      v-if="destination.type !== 'header'"
                    >
                      <div class="details">
                        <div class="cta-field">
                          <label class="default-input">
                            <span class="label">Headline</span>
                            <input
                              v-model="destination.text"
                              type="text"
                              v-if="destination.type !== 'message_optin'"
                              placeholder="Empty"
                            />
                            <textarea
                              v-else
                              v-model="destination.text"
                            ></textarea>
                          </label>
                        </div>
                        <div
                          class="cta-field"
                          v-if="destination.type !== 'message_optin'"
                        >
                          <label class="default-input">
                            <span class="label">Subtitle</span>
                            <input
                              v-model="destination.description"
                              type="text"
                              placeholder="Empty"
                            />
                          </label>
                        </div>
                      </div>
                      <div
                        class="details deeplinks"
                        v-if="destination.showDeeplinks"
                      >
                        <div class="cta-field">
                          <label class="default-input">
                            <span class="label">Apple Deeplink</span>
                            <input
                              v-model="destination.deeplink_ios"
                              type="text"
                              placeholder="Apple Deeplink"
                            />
                          </label>
                        </div>
                        <div class="cta-field">
                          <label class="default-input">
                            <span class="label">Android Deeplink</span>
                            <input
                              v-model="destination.deeplink_android"
                              type="text"
                              placeholder="Android Deeplink"
                            />
                          </label>
                        </div>
                      </div>
                      <template
                        v-if="
                          destination.configOptions &&
                            destination.configOptions.length
                        "
                      >
                        <div
                          class="config"
                          v-for="(configOption, i) in destination.configOptions"
                          :key="i"
                        >
                          <label class="default-input">
                            <span
                              class="label"
                              v-if="configOption.type !== 'toggle'"
                            >
                              {{ configOption.label }}
                            </span>
                            <input
                              v-if="configOption.type === 'input_short'"
                              v-model="destination.config[configOption.name]"
                              :placeholder="configOption.label"
                              type="text"
                            />
                            <textarea
                              v-else-if="configOption.type === 'input_long'"
                              v-model="destination.config[configOption.name]"
                              :placeholder="configOption.label"
                            ></textarea>
                            <div
                              class="toggle"
                              v-else-if="configOption.type === 'toggle'"
                            >
                              <div class="switch">
                                <input
                                  :checked="
                                    destination.config[configOption.name]
                                  "
                                  @change="
                                    event => {
                                      destination.config[configOption.name] =
                                        event.target.checked;
                                    }
                                  "
                                  type="checkbox"
                                  class="switch-input"
                                />
                                <span
                                  class="switch-background"
                                  data-on="On"
                                  data-off="Off"
                                ></span>
                                <span class="switch-handle"></span>
                              </div>
                              <span class="label">
                                {{ configOption.label }}
                              </span>
                            </div>
                          </label>
                        </div>
                      </template>
                      <div class="set-custom-image">
                        <input
                          type="file"
                          :ref="'filedestination' + index"
                          @change="onFileChange($event, destination)"
                          v-show="false"
                          accept="image/*"
                        />
                        <div class="set-custom-box">
                          <button
                            @click="uploadDestinationImage(index)"
                            class="button-text"
                            v-if="destination.type !== 'message_optin'"
                          >
                            <span v-if="destination.uploading"
                              >Uploading...</span
                            >
                            <span v-else>Replace image</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </draggable>
          </div>
          <div class="add-sources">
            <!-- <button
              class="button-secondary add-source"
              @click="addingLink = true"
            >
              <span>Add Link</span>
              <PlusIcon />
            </button> -->
            <div class="add-link-content">
              <div class="modal-content">
                <div class="des-background">
                  <div class="add-link">
                    <div class="title add-link-title">Add a Link</div>
                    <div class="url-field">
                      <span class="label enter-url">Enter URL</span>
                      <label class="default-input cus-add-link">
                        <input type="text" placeholder="URL" v-model="url" />
                        <button
                          class="button-default big"
                          @click="addLink(url)"
                          :disabled="fetching"
                        >
                          <span v-if="!fetching">Add</span>
                          <span v-else>Fetching...</span>
                        </button>
                      </label>
                    </div>
                  </div>
                  <div class="section">
                    <div class="header">
                      <div class="subs-card-head">
                        <div
                          class="subs-card"
                          @click="addDestination(headerDestination)"
                        >
                          <div class="icon h">
                            H
                          </div>
                          <p class="cus-sub-text">
                            {{ headerDestination.name }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="section" v-if="availablePlugins.length">
                    <div class="title enter-plg">Plugins</div>
                    <div class="subs-card-head">
                      <div
                        class="subs-card"
                        v-for="plugin in availablePlugins"
                        :key="plugin.type"
                        @click="addDestination(plugin)"
                      >
                        <img
                          :src="plugin.logo"
                          :alt="plugin.name"
                          class="subs-card-img"
                        />
                        <p class="cus-sub-text">{{ plugin.name }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="section" v-if="autoDetectedDestinations.length">
                    <div class="title enter-plg">Auto-Detected Links</div>
                    <div class="subs-card-head">
                      <div
                        class="subs-card"
                        v-for="destination in autoDetectedDestinations"
                        :key="destination.type"
                        @click="addDestination(destination)"
                        :class="{
                          'required-missing': isRequiredPostReleaseMissing(
                            destination.type
                          )
                        }"
                      >
                        <img
                          :src="getDestinationImage(destination)"
                          :alt="destination.name"
                          class="subs-card-img"
                        />
                        <p class="cus-sub-text">{{ destination.name }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="addingLink" class="modal">
          <div class="background-overlay"></div>
          <div class="modal-container">
            <div class="modal-close" @click="addingLink = false">
              <CloseIcon />
            </div>
            <div class="modal-content">
              <div class="add-link">
                <div class="title">Add Link</div>
                <div class="url-field">
                  <label class="default-input">
                    <span class="label">Enter URL</span>
                    <input type="text" placeholder="URL" v-model="url" />
                  </label>
                  <button
                    class="button-default big"
                    @click="addLink(url)"
                    :disabled="fetching"
                  >
                    <span v-if="!fetching">Add</span>
                    <span v-else>Fetching...</span>
                  </button>
                </div>
              </div>
              <div class="section">
                <div class="destinations">
                  <div
                    class="destination header"
                    @click="addDestination(headerDestination)"
                  >
                    <div class="icon h">
                      H
                    </div>
                    <div class="text">{{ headerDestination.name }}</div>
                  </div>
                </div>
              </div>
              <div class="section" v-if="availablePlugins.length">
                <div class="title">Plugins</div>
                <div class="destinations">
                  <div
                    class="destination"
                    v-for="plugin in availablePlugins"
                    :key="plugin.type"
                    @click="addDestination(plugin)"
                  >
                    <img :src="plugin.logo" :alt="plugin.name" class="icon" />
                    <div class="text">{{ plugin.name }}</div>
                  </div>
                </div>
              </div>
              <div class="section" v-if="autoDetectedDestinations.length">
                <div class="title">Auto-Detected Links</div>
                <div class="destinations">
                  <div
                    class="destination"
                    v-for="destination in autoDetectedDestinations"
                    :key="destination.type"
                    @click="addDestination(destination)"
                    :class="{
                      'required-missing': isRequiredPostReleaseMissing(
                        destination.type
                      )
                    }"
                  >
                    <img
                      :src="getDestinationImage(destination)"
                      :alt="destination.name"
                      class="icon"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="landing-page-preview">
          <div class="layouts">
            <div @click="$refs.select.click()" class="select">
              <label>
                <span class="label">Theme</span>
              </label>
              <select
                ref="select"
                v-model="layoutTheme"
                @change="layoutThemeUpdated"
                class="standard-select"
              >
                <option value="2-dark">Flat - Dark</option>
                <option value="2-light">Flat - Light</option>
              </select>
              <ChevronDownIcon class="pointer-events-none" />
              <span class="focus"></span>
            </div>
          </div>
          <div v-if="source" class="landing-page-preview-container">
            <LandingPagePreview
              :link="{ ...source, link_type: 'release' }"
              :destinations="edited_destinations"
              v-if="source.layout === 1"
            />
            <LandingPagePreviewTwo
              :link="{
                ...source,
                tracks: [
                  {
                    url:
                      source.preview_url ||
                      source.youtube_preview_url ||
                      source.custom_preview_url
                  }
                ],
                link_type: 'release'
              }"
              :destinations="edited_destinations"
              :postRelease="true"
              v-if="source.layout === 2"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="step-actions">
      <div @click="back()" class="back">
        <ArrowLeftIcon />
        <span class="back-text">BACK</span>
      </div>
      <button
        @click="finish()"
        :disabled="isLoading || !stepValidated"
        class="button-default big"
      >
        <span>Finish</span>
      </button>
    </div>
    <SnackBar>
      <template v-slot:icon></template>
    </SnackBar>
  </div>
</template>
<script>
import draggable from "vuedraggable";
// import PlusIcon from "../../../public/img/icons/plus.svg";
import RemoveIcon from "../../../public/img/icons/remove.svg";
import LandingPagePreview from "@/components/Custom/LandingPagePreview";
import LandingPagePreviewTwo from "@/components/Custom/LandingPagePreviewTwo";
import SnackBar from "@/components/Custom/SnackBar";
import ArrowLeftIcon from "../../../public/img/icons/arrow-left.svg";
import DraghandleIcon from "../../../public/img/icons/drag-2.svg";
import ChevronDownIcon from "../../../public/img/icons/chevron-down.svg";
import CloseIcon from "../../../public/img/icons/close.svg";

import {
  M_INSERT_LINK,
  M_DELETE_LINK_DESTINATION,
  M_INSERT_LINK_DESTINATION,
  M_UPDATE_LINK,
  M_UPDATE_LINK_DESTINATION,
  M_INSERT_BULK_MESSAGES,
  M_ADD_LINK_TRACK,
  M_DELETE_LINK_TRACK,
  M_UPDATE_LINK_TRACK_BY_PK,
  Q_META,
  Q_LINK_EDIT_PRESERVE
} from "@/graphql";

export default {
  name: "LinkWizardPreReleaseFinal",
  components: {
    ArrowLeftIcon,
    // PlusIcon,
    RemoveIcon,
    LandingPagePreview,
    LandingPagePreviewTwo,
    SnackBar,
    DraghandleIcon,
    draggable,
    ChevronDownIcon,
    CloseIcon
  },
  props: ["action", "destinations"],
  data() {
    return {
      isLoading: false,
      source: null,
      edited_destinations: [],
      layoutTheme: "",
      url: "",
      addingLink: false,
      fetching: false,
      destination_defaults: {
        spotify: {
          name: "Spotify",
          type: "spotify",
          default_cta: "Stream on Spotify",
          text: "Stream on Spotify"
        },
        applemusic: {
          name: "Apple Music",
          type: "applemusic",
          default_cta: "Stream on Apple Music",
          text: "Stream on Apple Music"
        },
        amazonmusic: {
          name: "Amazon Music",
          type: "amazonmusic",
          default_cta: "Play on Amazon Music",
          text: "Play on Amazon Music"
        },
        deezer: {
          name: "Deezer",
          type: "deezer",
          default_cta: "Play on Deezer",
          text: "Play on Deezer"
        },
        itunes: {
          name: "iTunes",
          type: "itunes",
          default_cta: "Buy on iTunes",
          text: "Buy on iTunes"
        },
        pandora: {
          name: "Pandora",
          type: "pandora",
          default_cta: "Play on Pandora",
          text: "Play on Pandora"
        },
        tidal: {
          name: "Tidal",
          type: "tidal",
          default_cta: "Play on Tidal",
          text: "Play on Tidal"
        },
        soundcloud: {
          name: "SoundCloud",
          type: "soundcloud",
          default_cta: "Play on SoundCloud",
          text: "Play on SoundCloud"
        },
        youtube: {
          name: "YouTube",
          type: "youtube",
          default_cta: "Watch on YouTube",
          text: "Watch on YouTube"
        },
        bandcamp: {
          name: "Bandcamp",
          type: "bandcamp",
          default_cta: "Download on Bandcamp",
          text: "Download on Bandcamp"
        },
        melon: {
          name: "Melon",
          type: "melon",
          default_cta: "Play on Melon",
          text: "Play on Melon"
        },
        resso: {
          name: "Resso",
          type: "resso",
          default_cta: "Play on Resso",
          text: "Play on Resso"
        },
        custom: {
          name: "Custom",
          type: "custom",
          text: "Custom",
          logo: null,
          url: null,
          default_cta: "Play",
          cta: "Play",
          enabled: true,
          new: true,
          expand: true
        },
        message_optin: {
          name: "SMS/Email Opt-In",
          type: "message_optin",
          default_cta:
            "Get notified about new releases, merch, and event drops",
          cta: "SMS/Email Opt-In",
          url: "#",
          text: "Get notified about new releases, merch, and event drops",
          description: "SMS/Email Opt-In",
          expand: false,
          configOptions: [
            {
              name: "email_capture",
              type: "toggle",
              label: "Capture email address"
            },
            {
              name: "phone_number_capture",
              type: "toggle",
              label: "Capture phone number"
            }
          ],
          config: {
            email_capture: true,
            phone_number_capture: true,
            success_message: ""
          }
        },
        header: {
          name: "Header",
          type: "header",
          default_cta: "Header",
          cta: "Header",
          text: null,
          url: "#"
        }
      },
      plugins: {
        message_optin: {
          name: "SMS/Email Opt-In",
          type: "message_optin",
          default_cta:
            "Get notified about new releases, merch, and event drops",
          cta: "SMS/Email Opt-In",
          url: "#",
          text: "Get notified about new releases, merch, and event drops",
          description: "SMS/Email Opt-In",
          expand: false,
          logo: "/img/destinations/contact.svg",
          configOptions: [
            {
              name: "email_capture",
              type: "toggle",
              label: "Capture email address"
            },
            {
              name: "phone_number_capture",
              type: "toggle",
              label: "Capture phone number"
            }
          ],
          config: {
            email_capture: true,
            phone_number_capture: true,
            success_message: ""
          }
        }
      }
    };
  },
  filters: {
    placeholderFilter({ type }) {
      if (
        ["spotify", "applemusic", "deezer", "amazonmusic", "itunes"].includes(
          type
        )
      )
        return "URL - Automatically Detected";
      else return "URL";
    }
  },
  created() {
    if (
      this.action === "edit" &&
      !this.$store.state.editLinkWizardData.destinations.length
    ) {
      this.$store.commit("setEditLinkWizardData", {
        destinations: this.destinations.flatMap(el => {
          if (el.pre_release) return [];
          return [
            {
              ...this.destination_defaults[el.type],
              name:
                el.type === "custom"
                  ? "Custom"
                  : this.destination_defaults[el.type]?.name,
              ...el,
              text:
                el.text || this.destination_defaults[el.type]?.text || el.cta,
              enabled: true,
              expand: true,
              new: false,
              showDeeplinks: false
            }
          ];
        })
      });
    }

    this.source =
      this.action === "edit"
        ? { ...this.$store.state.editLinkWizardData }
        : { ...this.$store.state.createLinkWizardData };

    this.layoutTheme = this.source.layout + "-" + this.source.theme;

    if (this.source.destinations.length) {
      this.edited_destinations = [
        ...this.source.destinations.flatMap(el => {
          if (!el.pre_release) return [el];
          return [];
        })
      ];
    }

    for (const [key, destination] of Object.entries(
      this.destination_defaults
    )) {
      if (this.edited_destinations.find(el => el.type === key)) continue;

      const preReleaseDestination = this.source.preReleaseDestinations.find(
        el => el.type === key && el.enabled
      );

      if (!preReleaseDestination || preReleaseDestination.type === "custom")
        continue;

      this.edited_destinations.push({
        ...preReleaseDestination,
        ...destination,
        description: null,
        cta: destination.default_cta,
        enabled: true,
        expand: true,
        pre_release: false,
        showDeeplinks: false,
        redirect_url: null
      });
    }
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    stepValidated() {
      return (
        this.edited_destinations.every(el => {
          if (el.enabled && !el.cta) return false;
          if (el.type === "message_optin" && el.enabled && el.config)
            return true;
          if (el.type === "header" && el.enabled && el.text) return true;
          if (
            [
              "spotify",
              "applemusic",
              "deezer",
              "itunes",
              "amazonmusic"
            ].includes(el.type) &&
            !el.url
          )
            return true;

          if (el.enabled && !el.url) return false;

          return el.enabled ? this.destinationUrlValid(el.type, el.url) : true;
        }) &&
        this.edited_destinations.some(
          el =>
            !el.pre_release &&
            el.enabled &&
            [
              "spotify",
              "applemusic",
              "deezer",
              "itunes",
              "amazonmusic"
            ].includes(el.type)
        )
      );
    },
    enabledDestinations() {
      return this.edited_destinations.filter(x => x.enabled);
    },
    autoDetectedDestinations() {
      return [
        "spotify",
        "applemusic",
        "deezer",
        "amazonmusic",
        "itunes"
      ].flatMap(type => {
        if (
          this.edited_destinations.some(
            edited_destination =>
              edited_destination.type === type && edited_destination.enabled
          )
        )
          return [];

        return [
          {
            ...this.destination_defaults[type],
            description: null,
            cta: this.destination_defaults[type].default_cta,
            enabled: true,
            expand: true,
            pre_release: false,
            showDeeplinks: false
          }
        ];
      });
    },
    requiredPostReleaseDestinationTypes() {
      const requiredTypes = ["spotify", "applemusic", "itunes"];

      return requiredTypes.filter(type =>
        this.linkWizardData.preReleaseDestinations.some(
          destination => destination.enabled && destination.type === type
        )
      );
    },
    missingRequiredPostReleaseDestinationTypes() {
      return this.requiredPostReleaseDestinationTypes.filter(
        type =>
          !this.edited_destinations.some(
            destination => destination.enabled && destination.type === type
          )
      );
    },
    availablePlugins() {
      return Object.values(this.plugins).filter(plugin => {
        return !this.edited_destinations.some(
          edited_destination =>
            edited_destination.type === plugin.type &&
            edited_destination.enabled
        );
      });
    },
    messageOptinEnabled() {
      return this.edited_destinations.some(
        el => el.type === "message_optin" && el.enabled
      );
    },
    linkWizardData() {
      return this.action === "edit"
        ? { ...this.$store.state.editLinkWizardData }
        : { ...this.$store.state.createLinkWizardData };
    },
    headerDestination() {
      return this.destination_defaults.header;
    }
  },
  methods: {
    getDestinationImage(destination) {
      if (destination.type === "message_optin") {
        return "/img/destinations/contact.svg";
      } else {
        return (
          destination.logo || `/img/destinations/${destination.type}_2.png`
        );
      }
    },
    back() {
      this.$store.commit("setCreateLinkWizardData", {
        ...this.source,
        destinations: [
          ...this.edited_destinations,
          ...this.source.preReleaseDestinations
        ]
      });
      this.$emit("back");
    },
    async finish() {
      if (this.missingRequiredPostReleaseDestinationTypes.length) {
        const labelMap = {
          spotify: "Spotify",
          applemusic: "Apple Music",
          itunes: "iTunes"
        };
        const missingLabels = this.missingRequiredPostReleaseDestinationTypes.map(
          type => labelMap[type] || type
        );

        await this.$store.dispatch("snack", {
          type: "error",
          text: `Add required post-release destinations: ${missingLabels.join(
            ", "
          )}.`
        });
        return;
      }

      const link_id =
        this.action === "edit"
          ? await this.updateLink()
          : await this.insertLink();

      this.$emit("finish", link_id);
    },
    isRequiredPostReleaseMissing(type) {
      return this.missingRequiredPostReleaseDestinationTypes.includes(type);
    },
    layoutThemeUpdated(e) {
      const [layout, theme] = e.target.value.split("-");
      this.source.layout = Number(layout);
      this.source.theme = theme;
    },
    getPlatform(url) {
      const musicServices = [
        { type: "amazonmusic", domain: "music.amazon.com" },
        { type: "applemusic", domain: "music.apple.com" },
        { type: "bandcamp", domain: "bandcamp.com" },
        { type: "deezer", domain: "deezer.com" },
        { type: "itunes", domain: "itunes.apple.com" },
        { type: "melon", domain: "melon.com" },
        { type: "pandora", domain: "pandora.com" },
        { type: "resso", domain: "resso.app" },
        { type: "soundcloud", domain: "soundcloud.com" },
        { type: "spotify", domain: "spotify.com" },
        { type: "tidal", domain: "tidal.com" },
        { type: "youtube", domain: "youtube.com" }
      ];

      for (let service of musicServices) {
        if (url.includes(service.domain)) {
          return service.type;
        }
      }

      return "custom";
    },
    async addLink(url) {
      this.fetching = true;

      let adjustedUrl = url;
      const pathParts = new URL(url).pathname.split("/");
      if (url.includes("spotify.com") && pathParts.length > 3) {
        adjustedUrl = `https://open.spotify.com/${pathParts
          .slice(2)
          .join("/")}`;
      }

      const r = await this.$apollo.query({
        query: Q_META,
        client: "localClient",
        variables: { url: adjustedUrl }
      });

      const metaData = JSON.parse(r.data.meta.data);

      console.log("metaData", metaData);

      const platform = this.getPlatform(url);

      const destination = {
        ...this.destination_defaults[platform],
        url: adjustedUrl,
        text:
          this.destination_defaults[platform === "custom" ? "" : platform]
            ?.text ||
          metaData.og_title ||
          "Custom",
        logo: metaData.domain_image || null,
        cta: this.destination_defaults[platform].default_cta,
        deeplink_android: metaData.deeplinks?.android || null,
        deeplink_ios: metaData.deeplinks?.ios || null,
        showDeeplinks: false
      };

      this.addDestination(destination);
      this.fetching = false;
    },
    addDestination(destination) {
      this.edited_destinations.push({
        ...destination,
        enabled: true,
        expand: true,
        showDeeplinks: false
      });

      this.addingLink = false;
      this.url = "";
    },
    uploadDestinationImage(index) {
      this.$refs[`filedestination${index}`][0].click();
    },
    async resetDestination(index) {
      if (!this.edited_destinations[index].url) {
        this.edited_destinations[index].logo = this.destination_defaults[
          this.edited_destinations[index].type
        ].logo;

        return;
      }

      this.edited_destinations[index].resetting = true;

      const r = await this.$apollo.query({
        query: Q_META,
        client: "localClient",
        variables: { url: this.edited_destinations[index].url }
      });

      const metaData = JSON.parse(r.data.meta.data);

      this.edited_destinations[index].logo = metaData.domain_image || null;
      this.edited_destinations[index].deeplink_android =
        metaData.deeplinks?.android || null;
      this.edited_destinations[index].deeplink_ios =
        metaData.deeplinks?.ios || null;
      this.edited_destinations[index].resetting = false;
    },
    removeDestination(index) {
      this.edited_destinations[index].enabled = false;
    },
    toggleShowDeeplinks(index) {
      this.edited_destinations[index].showDeeplinks = !this.edited_destinations[
        index
      ].showDeeplinks;
    },
    onFileChange(e, destination) {
      destination.uploading = true;
      this.addFile(e.target.files, destination);
    },
    async addFile(files, destination) {
      if (!files) return;
      const file = files[0];
      if (file.type.split("/")[0] !== "image") {
        destination.uploading = false;
        return await this.$store.dispatch("snack", {
          type: "error",
          text: "The file has to be of type 'image'"
        });
      }
      if (file.size > 1000000) {
        destination.uploading = false;
        return await this.$store.dispatch("snack", {
          type: "error",
          text: "The file can't be larger than 1MB"
        });
      }
      this.uploadFile(file, destination);
    },
    uploadFile(file, destination) {
      let _this = this;
      return new Promise(function(resolve, reject) {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append("file", file);

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              destination.uploading = false;
              const response = JSON.parse(xhr.responseText);
              destination.logo = response.url;
              resolve(xhr);
            } else {
              destination.uploading = false;
              _this.$store.dispatch("snack", {
                type: "error",
                text: "Upload failed, please try again."
              });
              reject(xhr);
            }
          }
        };

        xhr.open("POST", "/api/bunny-upload");
        xhr.send(formData);
      });
    },
    async insertLink() {
      try {
        this.isLoading = true;
        let destinationsInsert = [];
        const link = this.source;

        const destinations = [
          ...this.edited_destinations,
          ...this.linkWizardData.preReleaseDestinations
        ].flatMap(el => (el.enabled ? [el] : []));

        console.log(link, destinations);
        let priority = 1;
        for (let i = 0; i < destinations.length; i++) {
          if (destinations[i].id) {
            delete destinations[i].id;
          }

          if (
            destinations[i].url ||
            ([
              "spotify",
              "applemusic",
              "deezer",
              "text",
              "itunes",
              "amazonmusic",
              "message_optin",
              "header"
            ].includes(destinations[i].type) &&
              destinations[i].enabled)
          ) {
            if (destinations[i].type === "itunes") {
              destinations[i].url = (destinations[i].url || "").replace(
                "music.apple.com",
                "itunes.apple.com"
              );
            }

            let destination = {
              type: destinations[i].type,
              url: destinations[i].url,
              cta: destinations[i].cta,
              text: destinations[i].text,
              description: destinations[i].description,
              logo: destinations[i].logo,
              pre_release: !!destinations[i].pre_release,
              enabled: destinations[i].enabled,
              config: destinations[i].config,
              redirect_url: destinations[i].redirect_url || null,
              priority
            };

            if (destinations[i].type === "text") {
              destination.bulk_messages = {
                data: {
                  artist_id: this.artist.id,
                  platform: "sms",
                  message: destinations[i].config.message_body,
                  sms_link: `https://${link.domain}/${link.path}`
                },
                on_conflict: {
                  constraint: "bulk_messages_destination_id_key",
                  update_columns: ["message"]
                }
              };
            }

            destinationsInsert.push(destination);

            priority++;
          }
        }
        destinationsInsert = { data: destinationsInsert };
        const tracksInsert = { data: link.tracks };

        // if (link.image && !link.image.includes("b-cdn.net")) {
        //   try {
        //     const response = await fetch(link.image);
        //     const blob = await response.blob();
        //     const formData = new FormData();
        //     formData.append("file", blob);

        //     const uploadResponse = await fetch("/api/bunny-upload", {
        //       method: "POST",
        //       body: formData
        //     });

        //     if (!uploadResponse.ok) {
        //       throw new Error("Failed to upload image to Bunny CDN");
        //     }

        //     const { url } = await uploadResponse.json();
        //     link.image = url;
        //   } catch (err) {
        //     console.error("Error uploading image to Bunny:", err);
        //     await this.$store.dispatch("snack", {
        //       type: "error",
        //       text: "Failed to upload image. Please try again."
        //     });
        //     throw err;
        //   }
        // }

        console.log({
          artist_id: this.artist.id,
          domain: link.domain,
          path: link.path,
          image: link.image,
          title: link.title,
          subtitle: link.subtitle,
          post_release_title: link.post_release_title,
          post_release_subtitle: link.post_release_subtitle,
          release_name: link.release_name,
          release_time: link.release_time,
          offset: link.offset,
          timezone: link.timezone,
          description: link.description,
          preview_type: link.preview_type,
          theme: link.theme,
          display_artist_socials: link.display_artist_socials,
          destinations: destinationsInsert,
          tracks: tracksInsert,
          type: link.link_type,
          rid: link.rid,
          layout: link.layout,
          cover_image_style: link.cover_image_style
        });
        const r = await this.$apollo.mutate({
          mutation: M_INSERT_LINK,
          variables: {
            artist_id: this.artist.id,
            domain: link.domain,
            path: link.path.toLowerCase(),
            image: link.image,
            title: link.title,
            subtitle: link.subtitle,
            post_release_title: link.post_release_title,
            post_release_subtitle: link.post_release_subtitle,
            release_name: link.release_name,
            release_time: link.release_time,
            offset: link.offset,
            timezone: link.timezone,
            description: link.description,
            preview_type: link.preview_type,
            theme: link.theme,
            display_artist_socials: link.display_artist_socials,
            destinations: destinationsInsert,
            tracks: tracksInsert,
            type: link.link_type,
            rid: link.rid,
            layout: link.layout,
            cover_image_style: link.cover_image_style
          }
        });
        console.log({ r });
        return r.data.insert_links.returning[0].id;
        // await this.$router.push({
        //   name: "SmartLink",
        //   params: { id: link_id }
        // });
      } catch (err) {
        console.log(err);
        await this.$store.dispatch("snack", {
          type: "error",
          text: err.message.replace("GraphQL error: ", "")
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPreserveFields(linkId) {
      try {
        const { data } = await this.$apollo.provider.clients.httpClient.query({
          query: Q_LINK_EDIT_PRESERVE,
          variables: { link_id: linkId },
          fetchPolicy: "network-only"
        });
        const fresh = data.links_by_pk || {};
        const presaveJobsByType = {};
        (fresh.destinations || []).forEach(d => {
          if (d.presave_jobs != null) presaveJobsByType[d.type] = d.presave_jobs;
        });
        return { freshRid: fresh.rid ?? null, presaveJobsByType };
      } catch (err) {
        console.log("fetchPreserveFields failed", err);
        return { freshRid: this.source.rid ?? null, presaveJobsByType: {} };
      }
    },
    async updateLink() {
      try {
        this.isLoading = true;
        const promises = [];
        const link = this.source;

        const { freshRid, presaveJobsByType } = await this.fetchPreserveFields(
          link.id
        );

        const destinations = [
          ...this.edited_destinations,
          ...this.linkWizardData.preReleaseDestinations
        ];

        console.log({ link, destinations });
        let priority = 1;
        for (let i = 0; i < destinations.length; i++) {
          if (destinations[i].new) {
            delete destinations[i].id;
          }
          if (
            destinations[i].url ||
            ([
              "spotify",
              "applemusic",
              "deezer",
              "text",
              "itunes",
              "amazonmusic",
              "message_optin",
              "header"
            ].includes(destinations[i].type) &&
              destinations[i].enabled)
          ) {
            if (destinations[i].type === "itunes") {
              destinations[i].url = (destinations[i].url || "").replace(
                "music.apple.com",
                "itunes.apple.com"
              );
            }
            // UPDATE DESTINATION
            // url and id exists
            if (destinations[i].id) {
              const destinationUpdate = {
                id: destinations[i].id,
                type: destinations[i].type,
                url: destinations[i].url,
                cta: destinations[i].cta,
                text: destinations[i].text,
                description: destinations[i].description,
                logo: destinations[i].logo,
                pre_release: !!destinations[i].pre_release,
                enabled: destinations[i].enabled,
                config: destinations[i].config,
                redirect_url: destinations[i].redirect_url || null,
                deeplink_ios: destinations[i].deeplink_ios,
                deeplink_android: destinations[i].deeplink_android,
                priority
              };

              destinationUpdate.bulk_messages =
                destinations[i].type === "text"
                  ? [
                      {
                        artist_id: this.artist.id,
                        platform: "sms",
                        message: destinations[i].config.message_body,
                        sms_link: `https://${link.domain}/${link.path}`,
                        destination_id: destinations[i].id
                      }
                    ]
                  : [];

              console.log(destinationUpdate);

              let { bulk_messages, ...rest } = destinationUpdate;

              promises.push(
                this.$apollo.mutate({
                  mutation: M_UPDATE_LINK_DESTINATION,
                  variables: rest
                })
              );
              if (bulk_messages.length > 0)
                promises.push(
                  this.$apollo.mutate({
                    mutation: M_INSERT_BULK_MESSAGES,
                    variables: { bulk_messages }
                  })
                );
            } else {
              let destination = {
                link_id: link.id,
                type: destinations[i].type,
                cta:
                  destinations[i].cta ||
                  destinations.find(el => el.type === destinations[i].type)
                    .default_cta,
                url: destinations[i].url,
                text: destinations[i].text,
                logo: destinations[i].logo,
                description: destinations[i].description,
                pre_release: !!destinations[i].pre_release,
                enabled: destinations[i].enabled,
                config: destinations[i].config,
                redirect_url: destinations[i].redirect_url || null,
                deeplink_ios: destinations[i].deeplink_ios,
                deeplink_android: destinations[i].deeplink_android,
                presave_jobs: destinations[i].pre_release
                  ? null
                  : presaveJobsByType[destinations[i].type] || null,
                priority
              };

              if (destinations[i].type === "text") {
                destination.bulk_messages = {
                  data: {
                    artist_id: this.artist.id,
                    platform: "sms",
                    message: destinations[i].config.message_body,
                    sms_link: `https://${link.domain}/${link.path}`
                  },
                  on_conflict: {
                    constraint: "bulk_messages_destination_id_key",
                    update_columns: ["message"]
                  }
                };
              }
              promises.push(
                this.$apollo.mutate({
                  mutation: M_INSERT_LINK_DESTINATION,
                  variables: {
                    ...destination
                  }
                })
              );
            }
            priority++;
          } else if (destinations[i].id) {
            // DELETE DESTINATION
            // id and no url
            console.log(destinations[i]);
            promises.push(
              this.$apollo.mutate({
                mutation: M_DELETE_LINK_DESTINATION,
                variables: {
                  id: destinations[i].id
                }
              })
            );
          }
        }

        await Promise.all(promises);

        if (link.tracks?.[0]?.id) {
          const { id } = link.tracks[0];
          if (link.preview_type === "image" || link.preview_type === "null") {
            // need to delete track
            const r = await this.$apollo.mutate({
              mutation: M_DELETE_LINK_TRACK,
              variables: {
                id: id
              }
            });
            console.log("need to delete track", link, r);
          } else if (link.preview_type === "youtube") {
            // update youtube
            const r = await this.$apollo.mutate({
              mutation: M_UPDATE_LINK_TRACK_BY_PK,
              variables: {
                id: id,
                name: link.title,
                priority: 1,
                url: link.youtube_preview_url
              }
            });
            console.log("update youtube", link, r);
          } else if (link.preview_type === "custom") {
            // update custom
            const r = await this.$apollo.mutate({
              mutation: M_UPDATE_LINK_TRACK_BY_PK,
              variables: {
                id: id,
                name: link.title,
                priority: 1,
                url: link.custom_preview_url
              }
            });
            console.log("update custom", link, r);
          } else if (link.preview_type === "spotify") {
            // update spotify preview url
            const preview = await this.getSpotifyPreview(
              this.destinations_by_type.spotify.url
            );
            const r = await this.$apollo.mutate({
              mutation: M_UPDATE_LINK_TRACK_BY_PK,
              variables: {
                id: id,
                name: link.title,
                priority: 1,
                url: preview
              }
            });
            console.log("update spotify preview url", link, r);
          }
        } else {
          if (link.preview_type === "youtube") {
            // add youtube
            const r = await this.$apollo.mutate({
              mutation: M_ADD_LINK_TRACK,
              variables: {
                link_id: link.id,
                name: link.title,
                priority: 1,
                url: link.youtube_preview_url
              }
            });
            console.log("add youtube", link, r);
          } else if (link.preview_type === "custom") {
            // add custom
            const r = await this.$apollo.mutate({
              mutation: M_ADD_LINK_TRACK,
              variables: {
                link_id: link.id,
                name: link.title,
                priority: 1,
                url: link.custom_preview_url
              }
            });
            console.log("add custom", link, r);
          } else if (link.preview_type === "spotify") {
            // get spotify preview url
            const preview = await this.getSpotifyPreview(
              this.destinations_by_type.spotify.url
            );
            const r = await this.$apollo.mutate({
              mutation: M_ADD_LINK_TRACK,
              variables: {
                link_id: link.id,
                name: link.title,
                priority: 1,
                url: preview
              }
            });
            console.log("get spotify preview url", link, r);
          }
        }

        await this.$apollo.mutate({
          mutation: M_UPDATE_LINK,
          variables: {
            id: link.id,
            domain: link.domain,
            path: link.path.toLowerCase(),
            title: link.title,
            image: link.image,
            subtitle: link.subtitle,
            post_release_title: link.post_release_title,
            post_release_subtitle: link.post_release_subtitle,
            release_name: link.release_name,
            release_time: link.release_time,
            offset: link.offset,
            timezone: link.timezone,
            description: link.description,
            preview_type: link.preview_type,
            theme: link.theme,
            display_artist_socials: link.display_artist_socials,
            layout: link.layout,
            cover_image_style: link.cover_image_style,
            rid: link.rid || freshRid
          }
        });

        this.$store.commit("resetEditLinkWizardData");
      } catch (err) {
        console.log(err);
        await this.$store.dispatch("snack", {
          type: "error",
          text: err.message.replace("GraphQL error: ", "")
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.step-container {
  padding: 60px 40px 0 40px;

  .step-title {
    display: block;
    text-align: center;
    font-weight: bold;
    font-size: 30px;
    line-height: 42px;
    /* White */
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }

  .step-subtitle {
    display: block;
    text-align: center;
    margin-top: 6px;
    /* 16/Regular */
    font-size: 16px;
    line-height: 24px;
    /* Text/type2 */
    color: $text-type-2;
  }

  .step-content {
    margin-top: 30px;

    //width: 620px;
    .description {
      display: block;
      width: 100%;
      text-align: center;
      /* 16/Regular */
      font-size: 16px;
      line-height: 24px;
      /* Text/type0 */
      color: $text-type-0;
    }

    .step-layout {
      display: grid;
      grid-template-columns: auto 340px;
      grid-column-gap: 40px;
      margin-top: 36px;

      input {
        &::placeholder {
          font-style: italic;
        }
      }

      .destinations-container {
        button.add-source {
          width: 100%;
          margin-top: 30px;
          margin-bottom: 6px;
        }

        .destinations {
          .destination-container {
            display: flex;
            align-items: stretch;
            margin-bottom: 24px;

            .handle-icon {
              width: 40px;
              padding: 10px;
              display: flex;
              align-items: center;
              background-color: #100e2a;
              border-top-left-radius: 10px;
              border-bottom-left-radius: 10px;

              svg {
                width: 18px;
                path {
                  fill: white;
                }
              }

              &:hover {
                cursor: grab;

                svg {
                  path {
                    fill: $purple;
                  }
                }
              }
            }

            .destination {
              width: 100%;
              position: relative;
              padding: 16px 24px;
              background-color: #24224a;
              border-radius: 10px;
              border-top-left-radius: 0;
              border-bottom-left-radius: 0;

              .top {
                width: 100%;
                display: flex;
                align-items: center;

                .destination-logo {
                  display: flex;
                  justify-content: center;
                  background: #100e2a;
                  border: 1px solid #312f62;
                  padding: 5px;
                  border-radius: 5px;

                  img,
                  .h {
                    height: 51px;
                    border-radius: 10px;
                  }

                  .h {
                    width: 51px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background: linear-gradient(
                      46.19deg,
                      #9c27b0 5.66%,
                      #9733ee 94.35%
                    );
                    font-size: 35px;
                    color: #ffffff;
                  }
                }

                .url-field {
                  flex: 1;
                  margin: 0 6px;

                  input {
                    padding: 8px 10px;
                    line-height: 24px;
                  }
                }

                .destination-title {
                  font-weight: bolder;
                  font-size: 14px;
                  line-height: 42px;
                  color: #ffffff;
                  padding: 0 12px;
                  margin-left: 6px;
                  border-radius: 5px;
                  background: #100e2a;
                  border: 1px solid #312f62;
                }

                .destination-action {
                  background: #100e2a;
                  border: 1px solid #312f62;
                  width: 42px;
                  height: 42px;
                  border-radius: 5px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  margin: 0 3px;
                  margin-left: auto;

                  &:hover {
                    cursor: pointer;

                    svg {
                      path {
                        fill: white;
                      }
                    }
                  }

                  svg {
                    width: 24px;

                    path {
                      fill: $text-type-0;
                    }
                  }

                  &.active {
                    background: #9c27b0;
                    border-color: #ffffff;

                    svg {
                      path {
                        fill: white;
                      }
                    }
                  }
                }

                .destination-action_left {
                  margin-left: auto;
                }

                .dropdown-icon {
                  margin-left: 23px;
                  display: flex;
                  align-items: center;
                  cursor: pointer;

                  svg {
                    width: 20px;

                    path {
                      stroke: white;
                    }
                  }
                }

                .inverted {
                  transform: rotate(180deg);
                }
              }

              .destination-expanded {
                margin-top: 20px;

                .details {
                  margin-top: 10px;
                  display: flex;

                  .cta-field {
                    flex: 1;
                    margin: 0 3px;

                    input {
                      padding: 8px 10px;
                      line-height: 24px;
                    }

                    textarea {
                      resize: none;
                    }
                  }
                }

                .deeplinks {
                  background: #100e2a;
                  border: 1px solid #9c27b0;
                  border-radius: 10px;
                  padding: 12px;
                }

                .config {
                  margin-top: 15px;

                  label {
                    textarea {
                      resize: none;
                    }
                  }

                  .toggle {
                    display: flex;
                    margin-left: 6px;

                    .switch {
                      .switch-input:checked {
                        & ~ .switch-background {
                          border-color: #ffffff;
                          background-color: #9c27b0;
                        }

                        & ~ .switch-handle::before {
                          background: #ffffff;
                        }
                      }
                    }
                  }
                }

                .set-custom-image {
                  margin-top: 15px;
                  grid-area: 2 / 1 / 3 / 5;
                  display: flex;

                  .set-custom-box {
                    display: flex;
                    align-items: center;
                    /* BG/type2 */
                    background: $bg-type-1;
                    border-radius: 5px;

                    .upload-text {
                      margin-left: 12px;
                      margin-right: 66px;
                      /* 12/Regular */
                      font-size: 12px;
                      line-height: 30px;
                      /* Text/type1 */
                      color: $text-type-1;
                    }
                  }
                }
              }
            }
          }
        }

        .add-sources {
          max-width: 700px;
          width: 100%;
          margin-top: 40px;

          .add-source {
            display: inline;
            padding: 4px 16px;
            width: 100%;
            border-radius: 5px;
            margin: 0 10px 10px 0;

            span {
              font-size: 14px;
            }

            svg {
              width: 12px;
            }
          }
        }
      }

      .landing-page-preview {
        width: 355px;

        .layouts {
          display: flex;
          /* border: 1px solid #9c27b0; */
          border-radius: 5px;
          margin-bottom: 20px;

          .layout {
            width: 50%;
            font-size: 0.9rem;
            font-weight: 900;
            color: #8786ab;
            padding: 15px;
            text-align: center;
            cursor: pointer;
          }

          .selected {
            background-color: #9c27b0;
            color: white;
          }
        }

        .landing-page-preview-container {
          border-radius: 5px;
          overflow: hidden;
        }
      }
    }
  }

  .step-actions {
    margin-top: auto;
    padding-top: 50px;
    padding-bottom: 60px;
    display: flex;
    justify-content: space-between;

    .back {
      cursor: pointer;
      display: flex;
      //flex-direction: column;
      justify-content: flex-start;
      align-items: center;

      &:hover {
        svg {
          path {
            fill: white;
          }
        }

        .back-text {
          color: white;
        }
      }

      svg {
        width: 28px;

        path {
          fill: #8786ab;
        }
      }

      .back-text {
        font-weight: 600;
        font-size: 14px;
        line-height: 14px;
        text-transform: uppercase;
        /* Text/type2 */
        color: $text-type-2;
      }
    }

    .button-default {
      padding: 9px 40px;
    }
  }
}

.required-missing {
  outline: 1px solid #ff6b6b;
  box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.2);
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;

  .modal-container {
    padding: 0;
    width: 680px;
    z-index: 98;
    background: linear-gradient(
      241.25deg,
      rgba(41, 39, 85, 0.45) 4.4%,
      rgba(41, 39, 84, 0.85) 61.77%,
      rgba(27, 24, 66, 0.45) 119.94%
    );
    backdrop-filter: blur(23px);
    border-radius: 10px;

    .modal-close {
      cursor: pointer;
      position: absolute;
      top: 18px;
      right: 18px;
      display: flex;
      flex-direction: column;

      &:hover {
        svg {
          path {
            fill: white;
          }
        }

        .close-text {
          color: white;
        }
      }

      svg {
        width: 24px;

        path {
          fill: #8786ab;
        }
      }
    }

    .modal-content {
      display: flex;
      flex-direction: column;

      .add-link {
        padding: 30px;
        display: flex;
        flex-direction: column;

        .title {
          font-weight: 600;
          font-size: 18px;
          color: #ffffff;
        }

        .url-field {
          margin-top: 30px;
          display: flex;
          align-items: flex-end;

          .label {
            font-weight: 700;
            font-size: 14px;
            color: #ffffff;
          }

          button {
            margin-left: 10px;
          }
        }
      }

      .section {
        padding: 18px 33px;
        border-top: 1px solid #b1afcd;

        .title {
          font-weight: 700;
          font-size: 14px;
          color: #ffffff;
        }

        .destinations {
          width: 100%;
          margin-top: 20px;
          display: flex;
          flex-flow: row wrap;

          .destination {
            display: flex;
            flex-direction: column;
            margin-right: 16px;
            margin-bottom: 5px;
            cursor: pointer;

            &.header {
              align-items: center;
            }

            .icon {
              height: 70px;
              width: 70px;
              border-radius: 10px;

              &.h {
                display: flex;
                justify-content: center;
                align-items: center;
                background: linear-gradient(
                  46.19deg,
                  #9c27b0 5.66%,
                  #9733ee 94.35%
                );
                font-size: 45px;
                color: #ffffff;
              }
            }

            .text {
              font-weight: 600;
              font-size: 14px;
              color: #ffffff;
              margin-top: 5px;
            }
          }
        }
      }
    }
  }

  .background-overlay {
    z-index: 97;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: unset;
    /* Purple-dark */
    background: $purple-dark;
    opacity: 0.9;
    webkit-backdrop-filter: blur(10px) !important;
    backdrop-filter: blur(10px) !important;
  }
}

@media (max-width: 600px) {
  .step-container {
    width: 80%;
  }
  .landing-page-preview {
    display: none;
  }
}

.des-background {
  width: 100%;
  position: relative;
  padding: 24px;
  padding-bottom: 14px;
  background-color: #24224a;
  border-radius: 10px;
  margin: 0px auto;
}

.cus-add-link {
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: row;
  margin-top: 15px;
  margin-bottom: 25px;
  gap: 15px;
}

.cus-add-link input {
  width: 70% !important;
}

.enter-url {
  font-weight: 600;
}

.enter-plg {
  margin-bottom: 20px;
  font-weight: 600;
}

.add-link-title {
  margin-bottom: 30px;
  font-weight: 500;
  font-size: 22px;
}

.subs-card-head {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 35px;
}

.subs-card {
  padding: 5px 15px;
  border: 1px solid #6662af;
  border-radius: 10px;
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 10px;
  min-width: 155px;
  cursor: pointer;
}

.cus-sub-text {
  color: #fff;
  font-size: 14px;
}

.add-link-content .icon.h {
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(46.19deg, #9c27b0 5.66%, #9733ee 94.35%);
  font-size: 15px;
  color: #ffffff;
  height: 24px;
  width: 24px;
  border-radius: 5px;
  font-weight: 700;
}

img.subs-card-img {
  width: 40px;
}
.player-container-2 {
  overflow-y: none !important;
}

@media screen and (max-width: 1024px) {
  .step-container {
    padding: 60px 0px 0 0px;
  }
  .step-layout {
    grid-column-gap: 20px !important;
  }
}
@media screen and (max-width: 840px) {
  .step-layout {
    display: flex !important;
    flex-direction: column;
    gap: 30px;
    align-items: center;
  }
  .add-sources {
    max-width: inherit !important;
  }
}

@media screen and (max-width: 540px) {
  .destination {
    padding: 10px 8px !important;
  }
  .des-background {
    padding: 12px;
  }
}
</style>
